import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar, // Moved from NEW IMPORTS for cleaner diff, but still a new import
} from "@mui/material";
import MuiAlert from "@mui/material/Alert"; // Recommended way to import Alert for Snackbar content
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import dayjs from "dayjs";
import ListSubheader from "@mui/material/ListSubheader";

import { useDiverCertifications } from "../../hooks/useDiverCertifications.js";
import { useFormInput } from "../../hooks/useFormInput.js";

// Helper function to create a customizable Alert component for Snackbar
// This is typically done to allow 'severity' prop
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function UserCertifications() {
  const {
    diverCerts,
    agencyCerts,
    loading,
    error, // This 'error' is for initial fetches
    addDiverCertification,
    currentUser,
    userObjectId,
  } = useDiverCertifications();

  const {
    values: newCertData,
    handleChange,
    handleDateChange,
    resetForm,
  } = useFormInput({
    certificationId: "",
    certificationNumber: "",
    instructorName: "",
    instructorNumber: "",
    certificationDate: null,
    expirationDate: null,
    issuingShop: "",
    location: "",
    notes: "",
    status: "Active",
  });
  const [formOpen, setFormOpen] = useState(false);
  const [formError, setFormError] = useState(null); // This 'formError' is for form submission errors

  // --- SNACKBAR STATE ---
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // 'success', 'error', 'info', 'warning'

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };
  // --- END SNACKBAR STATE ---

  const handleOpenForm = () => {
    resetForm();
    setFormOpen(true);
    setFormError(null);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setFormError(null); // Clear form error when closing
  };

  const handleAddCertification = async (e) => {
    e.preventDefault();
    setFormError(null);
    try {
      await addDiverCertification(newCertData);
      handleCloseForm();
      showSnackbar("Certification added successfully!", "success");
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        "Failed to add certification. Please check your inputs.";
      setFormError(errorMessage); // Set form-specific error
      showSnackbar(errorMessage, "error"); // Show error in Snackbar as well
    }
  };

  const groupedAndSortedAgencyCerts = React.useMemo(() => {
    const grouped = agencyCerts.reduce((acc, cert) => {
      if (!acc[cert.agency]) {
        acc[cert.agency] = [];
      }
      acc[cert.agency].push(cert);
      return acc;
    }, {});

    Object.keys(grouped).forEach((agency) => {
      grouped[agency].sort((a, b) => a.name.localeCompare(b.name));
    });

    const sortedAgencies = Object.keys(grouped).sort();

    const flatList = [];
    sortedAgencies.forEach((agency) => {
      flatList.push({ type: "subheader", agencyName: agency });
      grouped[agency].forEach((cert) => {
        flatList.push({ type: "item", ...cert });
      });
    });

    return flatList;
  }, [agencyCerts]);

  // General loading/error conditions outside the form
  if (loading && (userObjectId || currentUser === undefined)) {
    return (
      <Container
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading certifications...
        </Typography>
      </Container>
    );
  }

  if (!currentUser && !userObjectId) {
    return (
      <Container sx={{ mt: 4, mb: 4, textAlign: "center" }}>
        <MuiAlert severity="warning" sx={{ mb: 2 }}>
          You need to be logged in to view and manage your certifications.
        </MuiAlert>
        <Typography variant="body1">
          Please log in to access this feature.
        </Typography>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          My Dive Certifications
        </Typography>

        {error && ( // General error from initial fetches
          <MuiAlert severity="error" sx={{ mb: 2 }}>
            {error}
          </MuiAlert>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenForm}
            sx={{
              backgroundColor: "primary.main",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            }}
          >
            Add New Certification
          </Button>
        </Box>

        {diverCerts.length === 0 && !loading && (
          <MuiAlert severity="info">
            You don't have any certifications recorded yet. Click "Add New
            Certification" to get started!
          </MuiAlert>
        )}

        {diverCerts.length > 0 && (
          <Paper
            elevation={3}
            sx={{ p: { xs: 2, md: 3 }, borderRadius: "8px" }}
          >
            <List>
              {diverCerts.map((cert) => (
                <ListItem
                  key={cert._id}
                  divider
                  sx={{
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: { xs: "flex-start", md: "center" },
                    py: { xs: 2, md: 1.5 },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="h6">
                        {/* Corrected: Display agency and certification name in primary text */}
                        {cert.certificationId ? (
                          <>
                            {cert.certificationId.agency} -{" "}
                            {cert.certificationId.name}
                          </>
                        ) : (
                          "N/A"
                        )}
                      </Typography>
                    }
                    secondary={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Level:{" "}
                          {cert.certificationId
                            ? cert.certificationId.level
                            : "N/A"}{" "}
                          &bull; Number: {cert.certificationNumber} &bull;
                          Status: {cert.status}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Earned:{" "}
                          {dayjs(cert.certificationDate).format("MMM D,YYYY")}
                          {cert.expirationDate &&
                            ` • Expires: ${dayjs(cert.expirationDate).format(
                              "MMM D,YYYY"
                            )}`}
                        </Typography>
                        {(cert.issuingShop || cert.location) && (
                          <Typography variant="body2" color="text.secondary">
                            {cert.issuingShop && `Shop: ${cert.issuingShop}`}
                            {cert.issuingShop && cert.location && ` • `}
                            {cert.location && `Location: ${cert.location}`}
                          </Typography>
                        )}
                        {cert.instructorName && (
                          <Typography variant="body2" color="text.secondary">
                            Instructor: {cert.instructorName}{" "}
                            {cert.instructorNumber
                              ? `(${cert.instructorNumber})`
                              : ""}
                          </Typography>
                        )}
                        {cert.notes && (
                          <Typography variant="body2" color="text.secondary">
                            Notes: {cert.notes}
                          </Typography>
                        )}
                      </Box>
                    }
                    sx={{ flexGrow: 1, mr: { md: 2 } }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {/* Add Certification Dialog Form */}
        <Dialog
          open={formOpen}
          onClose={handleCloseForm}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            Add New Certification
            <IconButton
              aria-label="close"
              onClick={handleCloseForm}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {formError && ( // Form specific error
              <MuiAlert severity="error" sx={{ mb: 2 }}>
                {formError}
              </MuiAlert>
            )}
            <Box
              component="form"
              onSubmit={handleAddCertification}
              sx={{ mt: 1 }}
            >
              <TextField
                select
                name="certificationId"
                label="Certification Type"
                value={newCertData.certificationId}
                onChange={handleChange}
                fullWidth
                required
                margin="normal"
                helperText="Select the type of certification you earned"
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 400,
                      },
                    },
                  },
                }}
              >
                {groupedAndSortedAgencyCerts.map((item) => {
                  if (item.type === "subheader") {
                    return (
                      <ListSubheader key={item.agencyName}>
                        {item.agencyName}
                      </ListSubheader>
                    );
                  } else {
                    return (
                      <MenuItem key={item._id} value={item._id}>
                        {item.name} ({item.level})
                      </MenuItem>
                    );
                  }
                })}
              </TextField>

              <TextField
                name="certificationNumber"
                label="Certification Number"
                value={newCertData.certificationNumber}
                onChange={handleChange}
                fullWidth
                required
                margin="normal"
                helperText="Your unique certification number"
              />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Certification Date"
                    value={
                      newCertData.certificationDate
                        ? dayjs(newCertData.certificationDate)
                        : null
                    }
                    onChange={(date) =>
                      handleDateChange("certificationDate", date)
                    }
                    slotProps={{
                      textField: { fullWidth: true, margin: "normal" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Expiration Date (Optional)"
                    value={
                      newCertData.expirationDate
                        ? dayjs(newCertData.expirationDate)
                        : null
                    }
                    onChange={(date) =>
                      handleDateChange("expirationDate", date)
                    }
                    slotProps={{
                      textField: { fullWidth: true, margin: "normal" },
                    }}
                  />
                </Grid>
              </Grid>

              <TextField
                name="instructorName"
                label="Instructor Name (Optional)"
                value={newCertData.instructorName}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
              <TextField
                name="instructorNumber"
                label="Instructor Number (Optional)"
                value={newCertData.instructorNumber}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
              <TextField
                name="issuingShop"
                label="Issuing Shop (Optional)"
                value={newCertData.issuingShop}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
              <TextField
                name="location"
                label="Location (Optional)"
                value={newCertData.location}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
              <TextField
                name="notes"
                label="Notes (Optional)"
                value={newCertData.notes}
                onChange={handleChange}
                fullWidth
                margin="normal"
                multiline
                rows={3}
              />
              <TextField
                select
                name="status"
                label="Status"
                value={newCertData.status}
                onChange={handleChange}
                fullWidth
                required
                margin="normal"
              >
                {["Active", "Expired", "Revoked", "Renewed"].map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </TextField>
              <DialogActions sx={{ px: 0, pt: 2 }}>
                <Button
                  onClick={handleCloseForm}
                  color="secondary"
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="contained" disabled={loading}>
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Add Certification"
                  )}
                </Button>
              </DialogActions>
            </Box>
          </DialogContent>
        </Dialog>

        {/* Snackbar Component */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000} // Snackbar will close after 6 seconds
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }} // Position at bottom center
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
}

export default UserCertifications;
